/**
 * Pre-Test Detection Composable
 * Automatically detects and shows PreTestModal for sessions with pending pre-tests
 * Similar to usePostTestDetection but for pre-tests (baseline assessment before tutoring)
 */

import { ref, onMounted, onUnmounted } from "vue";
import { auth, db } from "@/firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { logDebug, logInfo, logWarn, logError } from "@/utils/secureLogger";

interface PreTestSession {
  sessionId: string;
  sessionSubject: string;
  learningGoals: string;
  sessionDate: Date;
  tutorName: string;
  isMandatory?: boolean; // True for admin-created sessions (cannot be dismissed)
}

export function usePreTestDetection() {
  const showPreTestModal = ref(false);
  const pendingSession = ref<PreTestSession | null>(null);
  const isMandatoryPreTest = ref(false); // Track if current pre-test is mandatory
  let unsubscribe: (() => void) | null = null;
  const lastShownSessionId = ref<string | null>(null); // Track currently shown session to avoid duplicates

  /**
   * Start listening for sessions with pending pre-tests
   */
  const startDetection = () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      logWarn("Cannot start pre-test detection - no authenticated user");
      return;
    }

    logInfo("Starting pre-test detection for student", { userId: currentUser.uid });

    // Query for sessions where:
    // 1. Student is the current user
    // 2. pretestStatus is "pending"
    // 3. Session is confirmed (admin-created sessions are auto-confirmed)
    const sessionsRef = collection(db, "sessions");
    const q = query(
      sessionsRef,
      where("studentId", "==", currentUser.uid),
      where("pretestStatus", "==", "pending"),
      where("sessionStatus", "==", "confirmed")
    );

    unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        logDebug("Pre-test detection query result", {
          sessionCount: snapshot.docs.length,
        });

        // Process each session with pending pre-test
        for (const docSnapshot of snapshot.docs) {
          const sessionId = docSnapshot.id;
          const sessionData = docSnapshot.data();

          // Skip if currently showing this session's modal
          if (showPreTestModal.value && lastShownSessionId.value === sessionId) {
            continue;
          }

          // Check if pre-test exists and is incomplete
          const hasIncompletePreTest = await checkIncompletePreTest(sessionId);

          if (hasIncompletePreTest) {
            logInfo("Found session with pending pre-test", {
              sessionId,
              subject: sessionData.subject,
              isAdminCreated: true, // Admin-created sessions are always confirmed immediately
            });

            // Get tutor name
            const tutorName = await getTutorName(sessionData.tutorId);

            // ✅ Admin-created sessions have mandatory pre-tests (cannot be dismissed)
            const isMandatory = true;

            // Set up modal data
            pendingSession.value = {
              sessionId,
              sessionSubject: sessionData.subject || "Unknown Subject",
              learningGoals: sessionData.notes || "",
              sessionDate: sessionData.startAt?.toDate() || new Date(),
              tutorName,
              isMandatory, // Mark as mandatory
            };

            // Set mandatory flag
            isMandatoryPreTest.value = isMandatory;

            // Show modal
            showPreTestModal.value = true;
            lastShownSessionId.value = sessionId; // Track currently shown session

            logWarn("Showing mandatory pre-test modal for admin-created session", {
              sessionId,
              canDismiss: false,
            });

            // Only show one modal at a time
            break;
          }
        }
      },
      (error) => {
        logError("Error in pre-test detection listener", error);
      }
    );
  };

  /**
   * Check if pre-test exists but is incomplete
   */
  const checkIncompletePreTest = async (sessionId: string): Promise<boolean> => {
    try {
      const preTestRef = doc(db, "preTests", sessionId);
      const preTestDoc = await getDoc(preTestRef);

      if (!preTestDoc.exists()) {
        logWarn("Pre-test document not found for session", { sessionId });
        return false;
      }

      const preTestData = preTestDoc.data();

      // Check if pre-test is incomplete (no answers submitted)
      const isIncomplete =
        !preTestData.student_answers ||
        preTestData.student_answers.length === 0 ||
        !preTestData.date_completed;

      return isIncomplete;
    } catch (error) {
      logError("Error checking pre-test status", error);
      return false;
    }
  };

  /**
   * Get tutor name from user document
   */
  const getTutorName = async (tutorId: string): Promise<string> => {
    try {
      const tutorRef = doc(db, "users", tutorId);
      const tutorDoc = await getDoc(tutorRef);

      if (!tutorDoc.exists()) {
        return "Unknown Tutor";
      }

      const tutorData = tutorDoc.data();
      const firstName = tutorData.firstName || "";
      const lastName = tutorData.lastName || "";
      return `${firstName} ${lastName}`.trim() || "Unknown Tutor";
    } catch (error) {
      logError("Error fetching tutor name", error);
      return "Unknown Tutor";
    }
  };

  /**
   * Stop listening for pre-test changes
   */
  const stopDetection = () => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
      logDebug("Stopped pre-test detection");
    }
  };

  /**
   * Close the pre-test modal
   */
  const closeModal = () => {
    // Only allow closing if not mandatory
    if (isMandatoryPreTest.value) {
      logWarn("Attempted to close mandatory pre-test modal - ignoring");
      return;
    }

    showPreTestModal.value = false;
    pendingSession.value = null;
    lastShownSessionId.value = null; // Allow re-detection if needed
    isMandatoryPreTest.value = false;
  };

  /**
   * Mark pre-test as completed (update session document)
   */
  const markPreTestCompleted = async (sessionId: string) => {
    try {
      const sessionRef = doc(db, "sessions", sessionId);
      await updateDoc(sessionRef, {
        pretestStatus: "completed",
        pretestCompletedAt: Timestamp.now(),
      });

      logInfo("Pre-test marked as completed", { sessionId });
    } catch (error) {
      logError("Error marking pre-test as completed", error);
    }
  };

  // ✅ REMOVED: Auto-start on mount - App.vue will control when to start/stop
  // This allows detection to work continuously while user navigates student pages

  return {
    showPreTestModal,
    pendingSession,
    isMandatoryPreTest, // Expose mandatory flag
    startDetection,
    stopDetection,
    closeModal,
    markPreTestCompleted,
  };
}
